import { createContext, useContext, useEffect, useState, useCallback } from "react";
import api from "@/lib/api";

const PreferencesContext = createContext(null);

const DEFAULTS = {
  plain_language: false,
  read_aloud: false,
  examples_first: false,
  step_by_step: false,
  practice_first: false,
  short_lessons: false,
  larger_text: false,
  high_contrast: false,
  reduced_motion: false,
  onboarding_completed_at: null,
};

/**
 * Applies effective-immediately preferences to the <html> element so the
 * whole app inherits the styling.
 */
function applyHtmlClasses(prefs) {
  const html = document.documentElement;
  html.classList.toggle("cra3d-larger-text", !!prefs.larger_text);
  html.classList.toggle("cra3d-high-contrast", !!prefs.high_contrast);
  html.classList.toggle("cra3d-reduced-motion", !!prefs.reduced_motion);
}

export function PreferencesProvider({ children, enabled }) {
  const [prefs, setPrefs] = useState(DEFAULTS);
  const [loadedFor, setLoadedFor] = useState(null); // last enabled value we resolved

  const fetchPrefs = useCallback(async () => {
    if (!enabled) {
      setPrefs(DEFAULTS);
      applyHtmlClasses(DEFAULTS);
      setLoadedFor(false);
      return;
    }
    try {
      const { data } = await api.get("/preferences");
      setPrefs(data);
      applyHtmlClasses(data);
    } catch (_) {
      setPrefs(DEFAULTS);
    } finally {
      setLoadedFor(true);
    }
  }, [enabled]);

  useEffect(() => {
    fetchPrefs();
  }, [fetchPrefs]);

  // Clear applied classes when user logs out (enabled becomes false)
  useEffect(() => {
    if (!enabled) applyHtmlClasses(DEFAULTS);
  }, [enabled]);

  const update = async (patch) => {
    const { data } = await api.put("/preferences", patch);
    setPrefs(data);
    applyHtmlClasses(data);
    return data;
  };

  // Only consider prefs "loaded" if we successfully fetched for the CURRENT
  // enabled state. This prevents RequireOnboarded from reading stale DEFAULTS
  // during the (user=null -> user=X) auth-hydration transition on hard reload.
  const loaded = loadedFor === enabled;

  return (
    <PreferencesContext.Provider value={{ prefs, loaded, update, refresh: fetchPrefs }}>
      {children}
    </PreferencesContext.Provider>
  );
}

export function usePreferences() {
  const ctx = useContext(PreferencesContext);
  if (!ctx) throw new Error("usePreferences must be used inside <PreferencesProvider>");
  return ctx;
}
